import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { navLinks } from "@/mocks/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const onDark = !scrolled;
  const textColor = onDark ? "text-background-50" : "text-foreground-950";

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-background-50/90 backdrop-blur-xl border-b border-foreground-950/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="w-full px-5 md:px-10 lg:px-16">
          <div
            className={`flex items-center justify-between transition-all duration-500 ${
              scrolled ? "h-16" : "h-20 md:h-24"
            }`}
          >
            <Link
              to="/"
              className={`group flex items-baseline gap-[3px] ${textColor} transition-colors duration-500`}
              aria-label="CHEF.TV — home"
            >
              <span className="font-heading text-xl md:text-2xl font-semibold tracking-[0.02em]">
                CHEF
              </span>
              <span className="font-heading text-xl md:text-2xl font-semibold text-primary-500">
                .TV
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
              {navLinks.map((link) => {
                const external = Boolean(link.external && link.href);
                const active =
                  !external && link.to
                    ? location.pathname.startsWith(link.to)
                    : false;
                const linkClass = `link-underline font-label text-[11px] uppercase tracking-label transition-colors duration-300 ${
                  active
                    ? "text-primary-500"
                    : `${textColor} opacity-80 hover:opacity-100`
                }`;
                if (external) {
                  return (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${linkClass} inline-flex items-center gap-1`}
                    >
                      {link.short}
                      <i className="ri-arrow-right-up-line text-[11px]" />
                    </a>
                  );
                }
                return (
                  <Link key={link.label} to={link.to ?? "/"} className={linkClass}>
                    {link.short}
                  </Link>
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <Link
                to="/invite-eddie"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 font-label text-[10px] uppercase tracking-label transition-all duration-500 ${
                  onDark
                    ? "border-background-50/40 text-background-50 hover:bg-background-50 hover:text-foreground-950"
                    : "border-foreground-950/25 text-foreground-950 hover:bg-foreground-950 hover:text-background-50"
                }`}
              >
                Invite Eddie
              </Link>
            </div>

            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className={`lg:hidden flex flex-col items-end gap-[5px] p-2 ${textColor}`}
            >
              <span className="block h-px w-7 bg-current" />
              <span className="block h-px w-5 bg-current" />
              <span className="block h-px w-7 bg-current" />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-[60] bg-foreground-950 transition-opacity duration-500 lg:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between h-20 px-5">
            <span className="font-heading text-xl text-background-50">
              CHEF<span className="text-primary-500">.TV</span>
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="w-10 h-10 flex items-center justify-center text-background-50 text-2xl"
            >
              <i className="ri-close-line" />
            </button>
          </div>

          <nav className="flex flex-1 flex-col justify-center gap-1 px-6">
            {navLinks.map((link, i) => {
              const external = Boolean(link.external && link.href);
              const itemClass =
                "block py-2.5 font-heading text-2xl text-background-50 transition-all duration-500 hover:text-primary-400 hover:translate-x-2";
              const itemStyle = {
                transitionDelay: open ? `${120 + i * 60}ms` : "0ms",
                opacity: open ? 1 : 0,
                transform: open ? "none" : "translateY(16px)",
              };
              if (external) {
                return (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={itemClass}
                    style={itemStyle}
                  >
                    {link.label}
                    <i className="ri-arrow-right-up-line ml-2 align-middle text-base" />
                  </a>
                );
              }
              return (
                <Link
                  key={link.label}
                  to={link.to ?? "/"}
                  className={itemClass}
                  style={itemStyle}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="px-6 pb-10 flex flex-col gap-4">
            <Link
              to="/invite-eddie"
              className="btn-primary w-full"
            >
              Invite Eddie
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}